/**
 * ProtectedRoute Component
 * Protects routes based on permissions
 */

import { FC, ReactNode } from 'react';
import { Navigate } from '@tanstack/react-router';
import { Action, Subject } from '../types';
import { useCan } from '..';

interface ProtectedRouteProps {
  action: Action;
  subject: Subject;
  children: ReactNode;
  redirectTo?: string;
  fallback?: ReactNode;
}

/**
 * ProtectedRoute component for route-level permission checking
 *
 * @example
 * <ProtectedRoute action="access" subject="AdminPanel" redirectTo="/">
 *   <AdminDashboard />
 * </ProtectedRoute>
 */
export const ProtectedRoute: FC<ProtectedRouteProps> = ({
  action,
  subject,
  children,
  redirectTo = '/',
  fallback,
}) => {
  const can = useCan(action, subject);

  if (!can) {
    if (fallback) {
      return <>{fallback}</>;
    }
    return <Navigate to={redirectTo} />;
  }

  return <>{children}</>;
};
